/**
 * Main thread half of the link that pulls the standalone server's logs out of
 * the web worker it runs in.
 *
 * The worker's own console output never reaches this thread, so the server
 * bundle keeps a ring buffer and hands it over on request. That request travels
 * over a private MessageChannel rather than the worker's default port, so the
 * game's message plumbing never sees bug reporter traffic.
 */

const ConnectMessage = { __bugReport: 'connect' } as const;
const RequestTimeoutMs = 2000;

let port: MessagePort | null = null;
let pending = new Map<string, (logs: string | null) => void>();
let nextRequestId = 0;

/** Called by the client when it spins up the server worker. */
export function registerServerWorker(worker: Worker) {
	try {
		const channel = new MessageChannel();
		channel.port1.onmessage = (event: MessageEvent) => {
			const data = event.data;
			if (!data || typeof data !== 'object') return;
			const resolve = pending.get(data.requestId);
			if (resolve) {
				pending.delete(data.requestId);
				resolve(typeof data.logs === 'string' ? data.logs : null);
			}
		};
		channel.port1.start();
		worker.postMessage(ConnectMessage, [channel.port2]);
		port = channel.port1;
	} catch (e) {
		// A failure here only costs us the server logs section of the report.
		console.warn('Bug reporter could not attach to server worker', e);
	}
}

export function hasServerWorker() {
	return port !== null;
}

/** Resolves to null if there is no worker, or it doesn't answer in time. */
export function requestServerWorkerLogs(): Promise<string | null> {
	const activePort = port;
	if (!activePort) return Promise.resolve(null);

	return new Promise<string | null>(resolve => {
		const requestId = `req-${nextRequestId++}`;
		let settled = false;
		const finish = (logs: string | null) => {
			if (settled) return;
			settled = true;
			pending.delete(requestId);
			resolve(logs);
		};

		pending.set(requestId, finish);
		// A wedged or busy worker (the usual reason someone is filing a bug)
		// must not block the whole report.
		setTimeout(() => finish(null), RequestTimeoutMs);

		try {
			activePort.postMessage({ type: 'getLogs', requestId });
		} catch {
			finish(null);
		}
	});
}
